import React from "react";
import Drawer from "@mui/material/Drawer";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import Tooltip from "@mui/material/Tooltip";
import DrawerHeader from "../../requests/shared/helpers/DrawerHeader";
import DrawerFooterContainerSmall from "../../requests/shared/helpers/DrawerFooterContainerSmall";
import ButtonFilled from "../../requests/shared/helpers/ButtonFilled";
import ButtonOutlined from "../../requests/shared/helpers/ButtonOutlined";
import LoadSpinner from "../../../../utilities/LoadSpinner";
import { getInitials } from "../../requests/utilities/helpers";
import { getSubtaskNumberLabel } from "../plan_contents/subtaskNumbering";
import TaskResponseSummary from "../plan_contents/TaskResponseSummary";
import TaskPhotoPreview from "../plan_contents/TaskPhotoPreview";
import TaskNameCardEmployeeModal from "../plan_contents/TaskNameCardEmployeeModal";
import { VerdictPill } from "../plan_contents/TaskPassFailCompleted";
import StatusPill from "./StatusPill";
import { useTaskComments, CommentList, CommentComposer } from "./TaskConversation";
import TaskTimeline from "./TaskTimeline";
import {
  STATE_STYLES,
  occState,
  windowLabel,
  audienceLabel,
  cadenceLabel,
  cadenceKey,
  addDays,
  formatDateYMD,
  formatQhnum,
  timeOnly,
  dueLabel,
  durationLabel,
  locallyDone,
  workElapsedMs,
  workClockIsProvisional,
  bandTone,
  failedSteps,
  scoreLabel,
  canWorkOccurrence,
  canPinOccurrence,
  stepPhotos,
} from "./taskHubUtils";

// Right-hand detail for one task on one day: per-occurrence checklist state,
// who did what and when, validation notes, and the assign action. Managers
// can also open the checklist itself and finish the work from here.

// Historical audit items render as pass/fail even if their current template type changed.
export function getDrawerSubtaskNumberLabel(rows, index, stampedByUuid = {}) {
  return getSubtaskNumberLabel(rows, index, (row) =>
    stampedByUuid[row.plan_subtask?.uuid] ? "pass_fail" : row.plan_subtask?.type
  );
}

// onCreateTicket (optional): offered on a failed inspection item of a
// submitted audit; linkedFollowUps: the follow-ups already raised from it.
// stamped (optional): the item as it read when the audit was submitted,
// shown in place of the current wording so an edit since never rewrites
// what was audited.
export function SubtaskRow({ psa, numberLabel, onCreateTicket, linkedFollowUps, stamped }) {
  const [previewImage, setPreviewImage] = React.useState(null);
  // A stamped item was a pass/fail item when audited, whatever its type is
  // now, so its verdict (and the ticket action on a fail) stays.
  const sub = stamped
    ? { ...(psa.plan_subtask || {}), name: stamped.name, description: stamped.description, type: "pass_fail" }
    : psa.plan_subtask || {};
  const isPassFail = sub.type === "pass_fail";

  const done = locallyDone(psa);
  if (sub.type === "heading") {
    return (
      <div className="pt-3 pb-1">
        <div className="text-sm font-bold text-slate-700">{sub.name}</div>
        {sub.description && <div className="text-[11px] text-slate-400">{sub.description}</div>}
      </div>
    );
  }
  const photoUrls = stepPhotos(psa).map((p) => p.presigned_url).filter(Boolean);
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-slate-200 last:border-b-0">
      {numberLabel && <span className="shrink-0 text-sm leading-5 font-semibold text-slate-500 tabular-nums">{numberLabel}</span>}
      <div className="min-w-0 flex-1">
        <TaskResponseSummary
          subtask={{ ...psa, plan_subtask: sub }}
          completion={done}
          showAttribution={done}
          status={isPassFail ? <VerdictPill value={psa.value_boolean} size="xs" /> : undefined}
        />
        <div className="min-w-0">
          {(linkedFollowUps || []).map((f) => (
            <span key={f.uuid} className="block text-[11px] text-slate-500 mt-1">
              {f.kind === "ticket"
                ? f.ticket
                  ? `Ticket #${f.ticket.ticket_number} · ${String(f.ticket.status || "").replace(/_/g, " ")}`
                  : "Ticket deleted"
                : f.task
                ? `Task #${f.task.number} · ${f.task.name}`
                : "Task deleted"}
            </span>
          ))}
          {onCreateTicket && isPassFail && psa.value_boolean === false && (
            <button
              type="button"
              onClick={() => onCreateTicket(psa)}
              className="mt-1.5 text-[11px] font-semibold text-violet-700 bg-violet-50 border border-violet-200 hover:bg-violet-100 rounded px-2 py-0.5"
            >
              Create ticket
            </button>
          )}
          {photoUrls.length ? (
            <span className="flex flex-wrap gap-1.5 mt-1.5">
              {photoUrls.map((url) => (
                <button
                  key={url}
                  onClick={() => setPreviewImage(url)}
                  title="Open full-size photo"
                  className="block"
                >
                  <img
                    src={url}
                    alt={sub.name}
                    className="h-16 rounded-lg border border-slate-200 object-cover hover:ring-2 hover:ring-violet-400"
                  />
                </button>
              ))}
            </span>
          ) : (
            sub.is_image_required && (
              <span className="flex w-fit items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 mt-1.5">
                <CameraAltIcon style={{ fontSize: "13px" }} />
                Photo required
              </span>
            )
          )}
        </div>
      </div>
      <TaskPhotoPreview src={previewImage} onClose={() => setPreviewImage(null)} />
    </div>
  );
}

const SLOT_LABELS = {
  done: "Done",
  donelate: "Done late",
  bad: "Overdue",
  waiting: "To validate",
  warn: "In progress",
  ack: "Acknowledged",
  idle: "Open",
};

// One intraday slot: status row that expands to that slot's own checklist.
// Each slot is its own occurrence, so working on it and assigning it are
// per slot, never for the whole day.
// `missed`: the slot's day is over and it can no longer be worked on, so the
// chip says Missed rather than Overdue, which would imply it can still be done.
function SlotRow({ occ, slotLabel, defaultExpanded, roleNames, canManage, onWork, onAssignSlot, workable, assignable, tracked, missed = false }) {
  const [expanded, setExpanded] = React.useState(defaultExpanded);
  const s = occState(occ);
  const st = STATE_STYLES[s] || STATE_STYLES.idle;
  // Rows whose team member is gone render as a blank avatar with no name.
  const members = (occ.plan_task_assignment_members || []).filter((m) => m.store_team_member);
  const subs = (occ.plan_subtask_assignments || []).filter((p) => p.plan_subtask?.type !== "heading");
  const subsDone = subs.filter((p) => p.is_completed || p.completed_at).length;
  const expandable = (occ.plan_subtask_assignments || []).length > 0;
  const showWork = canManage && !occ.submitted_at && !occ.paused && workable(occ);
  const showAssign = canManage && !occ.submitted_at && assignable(occ);

  const showClock = tracked && ((occ.work_seconds || 0) > 0 || occ.work_running);

  // Every piece before the count sits in a fixed-width column, empty or not,
  // so the pills, the avatars and the two buttons line up down the list
  // whatever each slot's state or names. The columns are budgeted for the
  // 450px drawer (about 370px inside its paddings): dot 10, time 68, status
  // 100, avatars 60, buttons 52, count 45, five 6px gaps. The work clock sits
  // under the time rather than in a column of its own, and the avatars
  // give way on a phone-width drawer.
  return (
    <div className="border-b border-slate-100 last:border-b-0">
      <button
        onClick={() => expandable && setExpanded((v) => !v)}
        className={`w-full flex items-center gap-1.5 py-2.5 text-left ${expandable ? "cursor-pointer" : "cursor-default"}`}
      >
        <span className={`w-2.5 h-2.5 rounded flex-none ${st.dot}`} />
        <span className="w-[4.25rem] flex-none min-w-0">
          <span className="block text-sm font-semibold text-slate-800 tabular-nums leading-tight">{slotLabel}</span>
          {showClock && (
            <span className="flex items-center gap-1 text-[10px] font-medium text-slate-500 tabular-nums leading-tight whitespace-nowrap">
              {occ.work_estimated ? "~" : ""}
              <WorkElapsed occ={occ} />
              {occ.work_running && (
                <Tooltip title="Running">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-600 animate-pulse" />
                </Tooltip>
              )}
            </span>
          )}
        </span>
        <span className="w-[6.25rem] flex-none">
          <span className={`inline-block text-xs font-bold rounded px-2 py-0.5 whitespace-nowrap ${st.chip}`}>
            {missed && s === "bad" ? "Missed" : SLOT_LABELS[s] || "Open"}
          </span>
        </span>
        <span className="w-[3.75rem] flex-none inline-flex items-center sm:hidden">
          {members.slice(0, 2).map((m, i) => (
            <Tooltip key={i} title={`${m.store_team_member?.full_name || ""}${m.claimed ? " (picked it up)" : ""}`}>
              <span
                className="bg-slate-200 text-slate-700 w-6 h-6 rounded-full flex items-center justify-center border-2 border-white -ml-1.5 first:ml-0 text-[9px] font-semibold"
              >
                {getInitials(m.store_team_member?.full_name)}
              </span>
            </Tooltip>
          ))}
          {members.length > 2 && (
            <Tooltip title={members.slice(2).map((m) => m.store_team_member?.full_name || "").join(", ")}>
              <span className="bg-slate-400 text-white w-6 h-6 rounded-full flex items-center justify-center border-2 border-white -ml-1.5 text-[9px] font-semibold">
                +{members.length - 2}
              </span>
            </Tooltip>
          )}
        </span>
        <span className="flex-none inline-flex items-center gap-1">
          {showWork ? (
            <Tooltip title="Work on this slot">
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  onWork(occ);
                }}
                className="text-violet-700 bg-white border border-violet-300 hover:bg-violet-50 w-6 h-6 rounded-full flex items-center justify-center cursor-pointer flex-none"
              >
                <PlayArrowIcon style={{ fontSize: "15px" }} />
              </span>
            </Tooltip>
          ) : (
            <span className="w-6 h-6 flex-none" />
          )}
          {showAssign ? (
            <Tooltip title={occ.is_pinned ? "Change who this slot is assigned to" : "Assign this slot to someone"}>
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  onAssignSlot(occ);
                }}
                className="text-slate-500 bg-white border border-slate-200 hover:border-violet-400 hover:text-violet-700 w-6 h-6 rounded-full flex items-center justify-center cursor-pointer flex-none"
              >
                <PersonAddAltIcon style={{ fontSize: "14px" }} />
              </span>
            </Tooltip>
          ) : (
            <span className="w-6 h-6 flex-none" />
          )}
        </span>
        {expandable && (
          <span className="ml-auto flex items-center gap-1.5 text-[11px] text-slate-400 tabular-nums">
            {subs.length > 0 && `${subsDone}/${subs.length}`}
            {expanded ? (
              <ExpandLessIcon className="text-slate-300" style={{ fontSize: "18px" }} />
            ) : (
              <ExpandMoreIcon className="text-slate-300" style={{ fontSize: "18px" }} />
            )}
          </span>
        )}
      </button>
      {expanded && expandable && (
        <div className="pl-[22px] pb-2">
          {(occ.plan_subtask_assignments || []).map((psa, index) => (
            <SubtaskRow key={psa.uuid} psa={psa} numberLabel={getDrawerSubtaskNumberLabel(occ.plan_subtask_assignments, index)} />
          ))}
        </div>
      )}
    </div>
  );
}

// Time on task for one occurrence, advancing from the payload's arrival once
// per open session and ticking each minute while anyone is running. Its own
// component so its hooks sit outside the drawer's early returns.
function WorkElapsed({ occ }) {
  const running = !!occ?.work_running;
  const [fetchedAtMs, setFetchedAtMs] = React.useState(() => Date.now());
  const [nowMs, setNowMs] = React.useState(() => Date.now());
  React.useEffect(() => {
    const t = Date.now();
    setFetchedAtMs(t);
    setNowMs(t);
  }, [occ?.uuid, occ?.work_seconds, occ?.work_running_count, occ?.work_running]);
  React.useEffect(() => {
    if (!running) return undefined;
    const interval = setInterval(() => setNowMs(Date.now()), 60000);
    return () => clearInterval(interval);
  }, [running]);
  // "~" once an open session's idle expiry has passed: the sweep decides the
  // true value, and this is a best guess until the drawer refetches.
  const approx = workClockIsProvisional(occ, nowMs);
  return <>{approx ? "~" : ""}{durationLabel(workElapsedMs(occ, fetchedAtMs, nowMs))}</>;
}

function TaskDetailDrawer({
  open,
  storeId,
  initialTab = "details",
  onCommented,
  loading,
  error,
  onRetry,
  tz,
  task,
  rollup,
  dateStr,
  todayStr,
  focusSlotIndex,
  roleNames,
  homeCompany,
  onClose,
  onAssign,
  onValidate,
  save_plan_task_assignment,
  canManage,
  // Scored audits: what a submitted occurrence led to, and the actions that
  // raise more (all owned by the host).
  followUps = [],
  onCreateTicket,
  onCreateFollowUpTask,
  onScheduleReaudit,
}) {
  // The occurrence the checklist modal is open for (a slot, or the whole task).
  const [workOcc, setWorkOcc] = React.useState(null);
  React.useEffect(() => {
    if (!open) setWorkOcc(null);
  }, [open]);
  // Stable identity: the modal resets its local edits when its task changes.
  const workTask = React.useMemo(
    () => (workOcc ? { ...workOcc, plan_task: workOcc.plan_task || task } : null),
    [workOcc, task]
  );

  // Details / Conversation / Timeline, as on a ticket. The tab lands where the
  // row was clicked from and resets whenever the drawer targets another day
  // or task.
  const [tab, setTab] = React.useState(initialTab || "details");
  React.useEffect(() => {
    setTab(initialTab || "details");
  }, [initialTab, open, task?.uuid, dateStr]);

  // Conversation and Timeline are the DAY's for an hourly task: one thread
  // and one history across its slots, each entry naming its slot. A new
  // comment still lands on one occurrence: the slot the drawer was opened
  // from, else the first with open work.
  const allOccs = rollup?.occs || [];
  const threadOcc =
    (focusSlotIndex != null && allOccs.find((o) => o.intraday_slot_index === focusSlotIndex)) ||
    allOccs.find((o) => !o.submitted_at) ||
    allOccs[0] ||
    null;
  const threadStoreId = threadOcc?.store_id || storeId;
  const wholeDay = allOccs.length > 1;
  const {
    comments,
    slots: daySlots,
    loading: commentsLoading,
    posting,
    post,
  } = useTaskComments({
    storeId: threadStoreId,
    // Fetched only once the tab is open; the row count covers the label.
    occurrenceUuid: tab === "conversation" ? threadOcc?.uuid : null,
    occurrenceDate: threadOcc?.date,
    onPosted: () => onCommented?.(),
    wholeDay,
  });
  const rowCount = wholeDay
    ? allOccs.reduce((n, o) => n + (o.comment_count || 0), 0)
    : threadOcc?.comment_count || 0;
  const commentCount = Math.max(comments.length, rowCount);
  const composerSlot = daySlots.find((s) => s.plan_task_assignment_uuid === threadOcc?.uuid);

  if (!open) {
    return <Drawer anchor="right" open={false} onClose={onClose} />;
  }

  if (loading || error || !task || !rollup) {
    return (
      <Drawer anchor="right" open={open} onClose={onClose}>
        <div className="w-[450px] h-full bg-white flex flex-col items-center justify-center gap-3">
          {loading ? (
            <LoadSpinner />
          ) : error ? (
            <>
              <p className="text-sm text-slate-500 px-6 text-center">
                This task could not be loaded.
              </p>
              <button
                onClick={onRetry}
                className="text-sm font-semibold text-violet-700 hover:text-violet-800"
              >
                Try again
              </button>
            </>
          ) : (
            <p className="text-sm text-slate-500 px-6 text-center">
              This task has no occurrence on {dateStr}.
            </p>
          )}
        </div>
      </Drawer>
    );
  }

  const occs = rollup.occs;
  // The occurrence's own opening time, which is right even for a slot the
  // current definition no longer produces.
  const slotLabelOf = (occ, i) =>
    occ.available_at
      ? timeOnly(new Date(occ.available_at), tz)
      : formatQhnum(
          (task.lower_bound_qhnum || 0) +
            ((occ.intraday_slot_index ?? i) * (task.intraday_repeat_interval_minutes || 0)) / 60
        );
  // Several occurrences on one day means slots to show, whatever the task's
  // CURRENT definition says: a task turned non-intraday keeps the slots it
  // already recorded, and that work has to stay reachable.
  const isIntraday = occs.length > 1;
  const primary = rollup.primaryOcc;
  // An intraday task has one lifecycle pill for the whole day, rolled up from
  // its slots in the same order a single occurrence is ranked.
  const intradayStatus =
    rollup.total && rollup.done === rollup.total
      ? "completed"
      : rollup.waiting
      ? "waiting_validation"
      : rollup.inProgress || rollup.done
      ? "in_progress"
      : rollup.acknowledged
      ? "acknowledged"
      : "new";
  const hasOpenWork = occs.some((o) => !o.submitted_at);
  const openOcc = occs.find((o) => !o.submitted_at) || primary;
  const due = primary ? dueLabel(primary, new Date(), tz) : null;
  // Time on task: the day payload carries the occurrence totals; per-assignee
  // seconds only ride on Now rows, so that part shows when it is there.
  const tracked = !!(task.track_time ?? primary?.plan_task?.track_time);
  const assigneeTimes = (primary?.assignees || [])
    .filter((a) => (a.work_seconds || 0) > 0)
    .map(
      (a) =>
        `${(a.full_name || "").split(" ")[0] || "Someone"} ${durationLabel(a.work_seconds * 1000)}`
    )
    .join(", ");
  const waiting = occs.find((o) => o.lifecycle_status === "waiting_validation");
  const note = occs.map((o) => o.validation_note).find(Boolean);
  // A submitted audit, recognised by its stamped score (the task's scoring
  // switch may have been turned off since). A scored task cannot repeat
  // during the day (the form and the backend refuse it), so the day's single
  // occurrence is the audit.
  const scoredOcc = !isIntraday && primary?.submitted_at && primary?.score_total != null ? primary : null;

  // Coloured against the bands the grade was stamped under, so a band edit
  // since never turns a historical grade grey.
  const bands = scoredOcc?.score_bands || task.grade_bands || primary?.plan_task?.grade_bands || [];
  const tone = scoredOcc ? bandTone(scoredOcc.score_grade, bands) : null;

  const fails = scoredOcc ? failedSteps(scoredOcc) : [];
  const followUpsByStep = {};
  (followUps || []).forEach((f) => {
    if (f.plan_subtask_assignment_uuid) {
      const key = f.plan_subtask_assignment_uuid;
      if (!followUpsByStep[key]) followUpsByStep[key] = [];
      followUpsByStep[key].push(f);
    }
  });

  const showFollowUps = !!scoredOcc && (canManage || (followUps || []).length > 0);
  const stampedByUuid = {};
  (scoredOcc?.audit_items || []).forEach((item) => {
    if (item?.uuid) stampedByUuid[item.uuid] = item;
  });
  // Nothing is raised from a submission a manager may still return.
  const awaitingDecision = scoredOcc?.lifecycle_status === "waiting_validation";
  // Pause only applies to the current business day; past occurrences are
  // history and the backend rejects pausing them too.
  // A routine's untouched past day is missed history; only today, work
  // someone started yesterday (the carry-over window), and one-offs can
  // still be worked on.
  const isPast = !!todayStr && dateStr < todayStr;
  const inCarryOver = !!todayStr && dateStr >= formatDateYMD(addDays(new Date(`${todayStr}T12:00:00`), -1));
  // ...and nothing is workable before it opens: the save path stamps
  // started_at and keeps the answers, then refuses the submit.
  const isOpenYet = (occ) => !occ?.available_at || new Date(occ.available_at) <= new Date();
  // A paused or drafted definition keeps its recorded work visible, and the
  // backend refuses every write to it, so neither Work nor Assign is offered.
  const taskIsActive = !task?.status || task.status === "active";
  // A DELETED task keeps status active so its in-flight work can be finished,
  // but it takes no new assignment, so Assign is gated separately from Work.
  // Assigning is a manager's act whatever the audience: it follows the
  // occurrence's own state and can_pin, not this manager's leave to
  // conduct the work (a manager outside a scored audit's audience still
  // hands it to an eligible admin).
  // Unlike work, a pin does not wait for the occurrence to open: a one-off
  // dated next week can be handed to someone today.
  const assignable = (occ) =>
    taskIsActive &&
    (!task?.is_archived || tookPart(occ)) &&
    (!isPast || cadenceKey(task) === "one_off" || (inCarryOver && !!occ?.started_at)) &&
    canPinOccurrence(occ) &&
    (task?.can_be_assigned ?? taskIsActive);

  // A DELETED task takes no new work: only an occurrence someone already took
  // part in can still be finished, which is what every save path allows.
  const tookPart = (occ) =>
    !!(occ?.acknowledged_at || occ?.started_at || occ?.submitted_at);
  // ...and only where the backend would take the work: a scored audit is
  // conducted by its audience alone, and the day payload says whether this
  // manager is in it.
  const occurrenceOpen = (occ) =>
    taskIsActive &&
    (!task?.is_archived || tookPart(occ)) &&
    isOpenYet(occ) &&
    (!isPast || cadenceKey(task) === "one_off" || (inCarryOver && !!occ?.started_at));
  const workable = (occ) => occurrenceOpen(occ) && canWorkOccurrence(occ);

  // The slot breakdown counts a slot as missed by the same per-slot rule the
  // pill and the slot chips use: overdue, not paused, and not still workable
  // on a past day (carry-over). Today's overdue slots still count.
  const missedCount = occs.filter(
    (o) => occState(o) === "bad" && !o.paused && !(isPast && occurrenceOpen(o))
  ).length;
  const intradayBreakdown =
    [
      rollup.done ? `${rollup.done} done` : null,
      missedCount ? `${missedCount} missed` : null,
      rollup.waiting ? `${rollup.waiting} waiting` : null,
    ]
      .filter(Boolean)
      .join(" · ") || null;

  // Missed: a past day with unsubmitted work that nobody can act on any more.
  // "Missed" is derived (like Overdue), not a stored lifecycle status, so the
  // pill would otherwise keep saying "Not started" on a day the grid already
  // calls missed. Mirrors the backend's cell_state: any past-due, unsubmitted
  // occurrence that is neither paused (parked on purpose) nor still open
  // (carry-over / one-off work) makes the day missed, whatever its sibling
  // slots are doing. A submitted slot awaiting validation is not "bad", so a
  // single waiting occurrence never reads as missed.
  const isMissed =
    isPast &&
    occs.some((o) => occState(o) === "bad" && !o.paused && !occurrenceOpen(o));



  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <div className="w-[450px] h-full bg-white relative">
        <div className="max-h-screen overflow-auto">
          <DrawerHeader
            title={task.name}
            onClose={onClose}
          />
          <div className="flex border-b border-slate-200 px-6 -mt-3 mb-4">
            {[
              ["details", "Details"],
              ["conversation", "Conversation"],
              ["timeline", "Timeline"],
            ].map(([key, label]) => (
              <button
                key={key}
                className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  tab === key
                    ? "border-violet-700 text-violet-700"
                    : "border-transparent text-slate-500 hover:text-slate-700"
                }`}
                onClick={() => setTab(key)}
              >
                {label}
                {key === "conversation" && commentCount > 0 && (
                  <span
                    className={`ml-1.5 text-xs rounded-full px-1.5 py-0.5 ${
                      tab === key ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {commentCount}
                  </span>
                )}
              </button>
            ))}
          </div>
          {tab !== "details" && (
            <div className="w-full px-6">
              {tab === "conversation" ? (
                <>
                  <CommentList comments={comments} loading={commentsLoading} />
                  <div className="h-[100px]"></div>
                </>
              ) : (
                threadOcc && (
                  <TaskTimeline
                    storeId={threadStoreId}
                    occurrenceUuid={threadOcc.uuid}
                    occurrenceDate={threadOcc.date}
                    tz={tz}
                    wholeDay={wholeDay}
                  />
                )
              )}
            </div>
          )}
          {tab === "details" && (<>
          <div className="w-full px-6">
            {task.description ? (
              <p className="text-sm text-slate-500 mb-4">{task.description}</p>
            ) : null}
            <div className="flex gap-2 flex-wrap items-center">
              <Meta>{cadenceLabel(task)}</Meta>
              <Meta>{windowLabel(task)}</Meta>
              <Meta>
                {rollup.pinned
                  ? `Assigned · ${rollup.pinnedNames.join(", ") || "specific people"}`
                  : audienceLabel(task, roleNames)}
              </Meta>
            </div>
            {/* Status on its own row under the meta chips (mirrors the mobile
                sheet): it is the one coloured pill, so it never shares a
                wrapped line with the chips. Intraday tasks get the same pill,
                rolled up across slots, with the slot breakdown beside it. */}
            {primary && (
              <div className="flex items-center gap-2 mt-2">
                <StatusPill
                  status={isMissed ? "missed" : isIntraday ? intradayStatus : primary.lifecycle_status || "new"}
                  paused={!isMissed && (isIntraday ? rollup.paused : primary.paused)}
                />
                {isIntraday && intradayBreakdown && (
                  <span className="text-[11px] font-semibold text-slate-500">{intradayBreakdown}</span>
                )}
              </div>
            )}

            {/* A missed day states when the work was due and nothing more: the
                pill already says Missed, and an overdue counter beside it reads
                as two verdicts. */}
            {due && !isIntraday && isMissed && (
              <p className="mt-3 text-sm text-slate-600">{due.secondary || `Was due ${due.primary}`}</p>
            )}
            {due && !isIntraday && !isMissed && (
              <p className={`mt-3 text-sm ${due.overdue ? "text-rose-600 font-semibold" : "text-slate-600"}`}>
                {due.overdue ? due.primary : `Due ${due.primary}`}
                {due.secondary ? <span className="block text-xs font-normal">{due.secondary}</span> : null}
              </p>
            )}

            {tracked && primary && !isIntraday && (
              <p className="mt-2 text-sm text-slate-600">
                Time on task: <WorkElapsed occ={primary} />
                {primary.work_running ? " · running" : ""}
                {primary.work_estimated ? " (estimated)" : ""}
                {assigneeTimes ? ` · ${assigneeTimes}` : ""}
              </p>
            )}

            {scoredOcc && (
              <div className={`mt-4 rounded-xl border px-4 py-3 flex items-center gap-4 ${tone.chip}`}>
                <span className="text-3xl font-extrabold leading-none">{scoredOcc.score_grade || "–"}</span>
                <span className="min-w-0">
                  <span className="block text-sm font-semibold">{scoreLabel(scoredOcc)}</span>
                  <span className="block text-[11px] opacity-80">
                    {fails.length === 1 ? "1 item failed" : `${fails.length} items failed`}
                    {scoredOcc.lifecycle_status === "waiting_validation" ? " · awaiting validation" : ""}
                    {scoredOcc.submitted_at_formatted ? ` · submitted ${scoredOcc.submitted_at_formatted}` : ""}
                  </span>
                </span>
              </div>
            )}

            {waiting && canManage && (
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                <div className="text-sm font-semibold text-amber-900">Waiting for your validation</div>
                <div className="text-xs text-amber-800 mt-0.5">
                  {waiting.submitted_at_formatted ? `Submitted ${waiting.submitted_at_formatted}. ` : ""}
                  Check the work below, then validate it or return it with a note.
                </div>
                <button
                  onClick={() => onValidate(waiting)}
                  className="mt-2.5 text-xs font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg px-3 py-1.5"
                >
                  Validate or return
                </button>
              </div>
            )}

            {note && (
              <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">Manager note</div>
                <div className="text-sm text-slate-700 mt-0.5">“{note}”</div>
              </div>
            )}

            <div className="mt-5 bg-white border border-slate-200 rounded-xl px-4 py-2">
              {isIntraday ? (
                (() => {
                  // Expand the slot the person came for (from a Now row), else
                  // the first one that still needs attention.
                  const states = occs.map((o) => occState(o));
                  let focusIdx = occs.findIndex((o) => o.intraday_slot_index === focusSlotIndex);
                  if (focusIdx === -1) {
                    focusIdx = states.findIndex((s) => s === "warn" || s === "idle" || s === "ack" || s === "bad");
                  }
                  if (focusIdx === -1) focusIdx = 0;
                  return occs.map((occ, i) => (
                    <SlotRow
                      tracked={tracked}
                      key={occ.uuid}
                      occ={occ}
                      slotLabel={
                        // The occurrence's own opening time, which is right even
                        // for a slot the current definition no longer produces.
                        occ.available_at
                          ? timeOnly(new Date(occ.available_at), tz)
                          : formatQhnum(
                              (task.lower_bound_qhnum || 0) +
                                ((occ.intraday_slot_index ?? i) *
                                  (task.intraday_repeat_interval_minutes || 0)) /
                                  60
                            )
                      }
                      defaultExpanded={i === focusIdx}
                      roleNames={roleNames}
                      canManage={canManage}
                      workable={workable}
                      onWork={(o) => setWorkOcc(o)}
                      onAssignSlot={(o) => onAssign(o.intraday_slot_index ?? i, o)}
                      assignable={assignable}
                      missed={isPast && !occ.paused && !occurrenceOpen(occ)}
                    />
                  ));
                })()
              ) : (primary?.plan_subtask_assignments || []).length > 0 ? (
                <>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 pt-2 pb-1">
                    Checklist
                  </div>
                  {(primary?.plan_subtask_assignments || []).map((psa, index) => (
                    <SubtaskRow
                      key={psa.uuid}
                      psa={psa}
                      numberLabel={getDrawerSubtaskNumberLabel(primary.plan_subtask_assignments, index, stampedByUuid)}
                      linkedFollowUps={followUpsByStep[psa.uuid]}
                      stamped={stampedByUuid[psa.plan_subtask?.uuid]}
                      onCreateTicket={scoredOcc && !awaitingDecision && canManage && onCreateTicket ? onCreateTicket : undefined}


                    />
                  ))}
                </>
              ) : (
                <p className="text-xs text-slate-400 py-2">
                  This task has no checklist. Whoever does it just marks the whole task complete.
                </p>
              )}
            </div>

            {showFollowUps && (
              <div className="mt-5">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 pb-1">
                  Follow-ups
                </div>
                <div className="bg-white border border-slate-200 rounded-xl px-4 py-1">
                  {(followUps || []).length === 0 && (
                    <p className="text-xs text-slate-400 py-2">Nothing raised from this audit yet.</p>
                  )}
                  {(followUps || []).map((f) => {
                    const target = f.kind === "ticket" ? f.ticket : f.task;
                    const label =
                      f.kind === "ticket"
                        ? target
                          ? `Ticket #${target.ticket_number} · ${target.title}`
                          : "Ticket (deleted)"
                        : target
                        ? `${f.kind === "reaudit" ? "Re-audit" : "Task"} #${target.number} · ${target.name}`
                        : `${f.kind === "reaudit" ? "Re-audit" : "Task"} (deleted)`;
                    const status = target
                      ? f.kind === "ticket"
                        ? String(target.status || "").replace(/_/g, " ")
                        : target.is_archived
                        ? "deleted"
                        : target.status
                      : "";
                    return (
                      <div
                        key={f.uuid}
                        className="flex items-start justify-between gap-3 py-2 border-b border-slate-100 last:border-b-0"
                      >
                        <span className="min-w-0">
                          <span className="block text-sm font-medium text-slate-800 truncate">{label}</span>
                          <span className="block text-[11px] text-slate-400">
                            {[f.step_name, f.created_by?.full_name, f.created_at_formatted].filter(Boolean).join(" · ")}
                          </span>
                        </span>
                        {status && (
                          <span className="text-[11px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5 whitespace-nowrap capitalize">
                            {status}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {canManage && awaitingDecision && (
                  <p className="text-[11px] text-slate-400 mt-2">
                    Validate this audit first; follow-ups are raised from a validated result.
                  </p>
                )}
                {canManage && !awaitingDecision && (
                  <div className="flex gap-2 mt-2">
                    {fails.length > 0 && onCreateFollowUpTask && (
                      <button
                        type="button"
                        onClick={() => onCreateFollowUpTask(scoredOcc)}
                        className="text-xs font-semibold text-violet-700 bg-white border border-violet-300 hover:bg-violet-50 rounded-lg px-3 py-1.5"
                      >
                        Create follow-up task
                      </button>
                    )}
                    {onScheduleReaudit && (
                      <button
                        type="button"
                        onClick={() => onScheduleReaudit(scoredOcc)}
                        className={`text-xs font-semibold rounded-lg px-3 py-1.5 border ${
                          scoredOcc.score_lowest_band
                            ? "text-rose-700 bg-rose-50 border-rose-200 hover:bg-rose-100"
                            : "text-slate-600 bg-white border-slate-200 hover:border-slate-400"
                        }`}
                      >
                        Schedule re-audit
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Each slot of an hourly task shows its own people; a task-level
                line would name whoever did one slot as if it were the task. */}

            {!isIntraday && rollup.pinnedMembers.length > 0 && (
              <p className="mt-3 text-xs text-slate-500">
                {rollup.pinnedMembers
                  .map((m) => {
                    const name = m.store_team_member?.full_name || "";
                    return m.claimed ? `${name} picked it up` : `${name} assigned`;
                  })
                  .join(" · ")}
              </p>
            )}

          </div>

          {/* On a missed day every button is gated off, so the footer says
              why instead of rendering as an empty bar. */}
          {canManage && hasOpenWork && !isIntraday && (
            <DrawerFooterContainerSmall>
              {isMissed ? (
                <p className="w-full text-center text-xs text-slate-500">This day has passed, so this task can no longer be worked on.</p>
              ) : (
              <div className="w-full flex gap-2">
                {openOcc && !openOcc.paused && workable(openOcc) && (
                  <div className="flex-1">
                    <ButtonOutlined text="Work on it" onClick={() => setWorkOcc(openOcc)} />
                  </div>
                )}
                {(openOcc
                  ? assignable(openOcc)
                  : !isPast && (task?.can_be_assigned ?? taskIsActive)) && (
                  <div className="flex-1">
                    <ButtonFilled
                      text={rollup.pinned ? "Change assignee" : "Assign"}
                      onClick={() => onAssign(null, openOcc)}
                    />
                  </div>
                )}
              </div>
              )}
            </DrawerFooterContainerSmall>
          )}
          {/* A mixed carry-over day (one slot missed, another started
              yesterday and still workable) keeps the working hint: saying
              nothing can be worked on beside a live Work button would lie. */}
          {canManage && hasOpenWork && isIntraday && (
            <p className="px-6 mt-4 text-xs text-slate-400">
              {isMissed && !occs.some(occurrenceOpen)
                ? "This day has passed, so this task can no longer be worked on."
                : "Each time slot is its own task: use the play button to work on a slot and the person button to assign it."}
            </p>
          )}
          </>)}
          <div className="h-[130px]"></div>
        </div>
        {tab === "conversation" && threadOcc && (
          <div className="w-full absolute bottom-0 bg-white border-t border-slate-300 px-6 py-4 z-30">
            {/* Keyed by occurrence: a draft typed for one slot is not carried
                into another, and a slow post cannot clear the next slot's text. */}
            <CommentComposer
              key={threadOcc.uuid}
              onPost={post}
              posting={posting}
              hint={wholeDay ? `Posting on the ${composerSlot?.slot_label || slotLabelOf(threadOcc, 0)} slot` : null}
            />
          </div>
        )}
      </div>

      {workOcc && (
        <TaskNameCardEmployeeModal
          open={!!workOcc}
          handleClose={() => setWorkOcc(null)}
          task={workTask}
          onCommented={() => onCommented?.()}
          isAssignedToEveryone={!workOcc.is_pinned}
          save_plan_task_assignment={save_plan_task_assignment}
          homeCompany={homeCompany}
          roleNames={roleNames}
          setShowLightbox={() => {}}
          setSelectedImage={() => {}}
        />
      )}
    </Drawer>
  );
}

function Meta({ children }) {
  return (
    <span className="inline-flex items-center text-xs font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-0.5">
      {children}
    </span>
  );
}

export default TaskDetailDrawer;
